import { createSlice } from '@reduxjs/toolkit';

import AbstractSliceHandler from 'modules/shared/stores/abstract-slice-handler';
import LocalEvent from 'utils/local-event';
import Log from 'utils/log';
import t from 'utils/translate';
import SnackbarTypes from 'constants/snackbar-types';
import LayoutManager from 'utils/layout-manager';
import {
    USER_SETTINGS_OPTIONS,
    GLOBAL_ACTIVE_INACTIVE,
    MY_BUSINESS_OPTIONS,
    DISTANCE_UNIT,
} from 'constants/user-settings';
import ConfigIniUtil from 'utils/config-ini-util';
import { addSnackbar } from 'utils/dialogs-manager';
import { MainMenuItemType } from 'constants/top-bar-constants';
import { getCompanySettings } from './company-settings-slice-handler';

const initialState = {
    dataIsLoading: true,
    dataIsUpdating: false,
    shortcutEditorData: {
        editedShortcut: null,
        deletedShortcut: null,
        canUseRecordedKey: false,
    },
    recordedShortcut: null,
    /**
     * This is the actual JSON object that is stored in DB for the user.
     * This JSON must contain here all default values of the features. These wil be used when there are new settings and
     * they are not yet in the DB JSON.
     * @type {UserSettingsJson}
     */
    settingsJson: {
        /**
         * ADVANCED-TELEPHONY: Anonymous CallOut
         */
        anonymousCallOut: 0,
        /**
         * AUTO-ANSWER: Ambient listening Auto-answer
         */
        ambientListeningAutoAnswer: 0,
        /**
         * AUTO-ANSWER: Call Auto-answer
         * 0 - Inactive
         * 1 - Any Call
         * 2 - First Call only
         * 3 - By Priority
         */
        callAutoresponse: 0,
        /**
         * OPTIONS: Advanced Channels Mode
         */
        channelsAdvancedMode: USER_SETTINGS_OPTIONS.CHANNELS_ADVANCED_MODE.STANDARD,
        /**
         * OPTIONS: Talk in Multiple Channels
         */
        channelsMultipleTalkingEnabled: GLOBAL_ACTIVE_INACTIVE.INACTIVE,
        /**
         * OPTIONS: Channels Playback Mode
         */
        channelsPlaybackMode: USER_SETTINGS_OPTIONS.CHANNELS_PLAYBACK_MODE.HEAR_ALL,
        /**
         * AUTO-ANSWER: Conference Call Auto-answer
         * 0 - Inactive
         * 1 - Any Call
         * 2 - First Call only
         * 3 - By Priority
         */
        conferenceCallAutoAnswer: 0,
        /**
         * OPTIONS: Conversations with On Duty contacts
         */
        conversationWithOnDutyContacts: GLOBAL_ACTIVE_INACTIVE.ACTIVE,
        /**
         * OPTIONS: Default action
         */
        defaultAction: USER_SETTINGS_OPTIONS.ACTIONS.MESSAGE,
        /**
         * MY BUSINESS: Default tab
         */
        defaultMyBusinessTab: MY_BUSINESS_OPTIONS.TABS.START,
        /**
         * OPTIONS: Default page
         */
        defaultPage: USER_SETTINGS_OPTIONS.PAGES.CONVERSATIONS,
        /**
         * OPTIONS: Default send button
         */
        defaultSendButton: USER_SETTINGS_OPTIONS.SEND_BUTTONS.SEND,
        /**
         * OPTIONS: Default contacts page tab
         */
        defaultContactsPageTab: USER_SETTINGS_OPTIONS.CONTACTS_PAGE_TABS.USERS,
        /**
         * GEOLOCATION MAP: Display user Location Settings popup notification on map
         */
        displayNotificationOnGeolocationMap: 0,
        /**
         * GEOLOCATION MAP: Select distance unit
         */
        distanceUnit: DISTANCE_UNIT.YARD,
        /**
         * AUTO-ANSWER: Emergency Ptt Call auto-Answer
         */
        emergencyPttCallAutoAnswer: 0,
        /**
         * GEOLOCATION MAP: Geolocation notification
         */
        geolocationNotification: 0,
        /**
         * OPTIONS: Hear Other Channels While Talking
         */
        hearOtherChannelsWhileTalking: GLOBAL_ACTIVE_INACTIVE.INACTIVE,
        /**
         * KEYBOARD SHORTCUTS
         */
        keyboardShortcuts: [
            {
                type: 'CALL_ANSWER',
                enabled: true,
                ctrlKey: true,
                shiftKey: true,
                code: 'PageUp',
            },
            {
                type: 'CALL_REJECT',
                enabled: true,
                ctrlKey: true,
                shiftKey: true,
                code: 'PageDown',
            },
            // this shortcut is no longer configurable
            // https://jira.streamwide.com/browse/SMARTMS-50693
            /*
            {
                type: 'FLOOR_CONTROL',
                enabled: true,
                ctrlKey: true,
                code: 'Space',
            },
             */
            {
                type: 'CHANNEL_MOVE_UP',
                enabled: true,
                ctrlKey: true,
                code: 'ArrowUp',
            },
            {
                type: 'CHANNEL_MOVE_DOWN',
                enabled: true,
                ctrlKey: true,
                code: 'ArrowDown',
            },
        ],
        /**
         * LANGUAGE: Select a language
         */
        language: 'en_US',
        /**
         * OPTIONS: Live Replay
         */
        liveReplay: GLOBAL_ACTIVE_INACTIVE.INACTIVE,
        /**
         * NOTIFICATIONS AND ALERTS: Message read confirmation
         */
        messageRead: 0,
        /**
         * NOTIFICATIONS AND ALERTS: Messages sound
         */
        messageSound: 'water_droplet',
        /**
         * NOTIFICATIONS AND ALERTS: My Business Notification Sound
         */
        myBusinessNotificationSound: 'water_droplet',
        /**
         * MY BUSINESS: Process Auto-Saving
         */
        myBusinessProcessAutosaving: 0,
        /**
         * MY BUSINESS: Process Auto-Saving
         */
        myBusinessConfirmationWindowDisplay: [],
        /**
         * NOTIFICATIONS AND ALERTS: Push-To-Talk sound
         */
        pttCallSound: 'ring_tone_standard',
        /**
         * OPTIONS: Sort contacts by
         */
        sortContactsBy: USER_SETTINGS_OPTIONS.SORT_CONTACTS_BY.FIRST_NAME,
        /**
         * NOTIFICATIONS AND ALERTS: Call sound
         */
        voipCallSound: 'ring_tone_standard',
        /**
         * AUTO-ANSWER: Push-To-Talk Auto-answer
         * 0 - Inactive
         * 1 - Any Call
         * 2 - First Call only
         * 3 - By Priority
         */
        walkieTalkieAutoresponse: 0,
        /**
         * OPTIONS: Push-To-Talk Take the floor mode
         */
        walkieTalkieTakeFloorMode: USER_SETTINGS_OPTIONS.PTT_TAKE_FLOOR_MODE.PTT_MODE,
        /**
         * OPTIONS: Channel confirmation windows displayed
         */
        channelsConfirmationWindowDisplay: [],
        /**
         * OPTIONS: Cross mute list (CSV list with msisdns)
         */
        crossMuteList: '',
    },
    settings: {
        profilePicture: {
            url: '',
            dataIsLoading: true,
            timestamp: 0,
        },
        alias: {
            dataIsSaving: false,
        },
        userCustomStatus: {
            dataIsSaving: false,
        },
        availability: {
            dataIsSaving: false,
        },
        operationalStatus: {
            dataIsSaving: false,
        },
        languageSelector: {
            // does not make sense to load the localized country names, right after the initial scripts loading,
            // if this was not actually requested by JSX
            localizedCountriesRequestedBeforeLayoutLoad: false,
            dataIsLoading: false,
            localizedCountriesNames: {},
        },
        callFwd: {
            dialogOpen: false,
        },
        userPassword: {
            dataIsSaving: false,
        },
        crossMuteList: {
            dialogOpen: false,
        },
    },
};

const shortcutProps = ['altKey', 'ctrlKey', 'metaKey', 'shiftKey', 'code'];

class UserSettingsSliceHandler extends AbstractSliceHandler {
    static getInstance() {
        if (!UserSettingsSliceHandler.instance) {
            UserSettingsSliceHandler.instance = new UserSettingsSliceHandler();
        }

        return UserSettingsSliceHandler.instance;
    }

    constructor() {
        super('userSettingsSlice');

        this.keyPressDebouncer = null;

        LocalEvent.setListener(LocalEvent.eventTypes.COMPANY_SETTINGS_LOADED, () => {
            this.onCompanySettingsUpdate();
        });
        LocalEvent.setListener(LocalEvent.eventTypes.COMPANY_SETTINGS_UPDATED, () => {
            this.onCompanySettingsUpdate();
        });
        LocalEvent.setListener(LocalEvent.eventTypes.CALL_FWD_SETTINGS_UPDATED_EXTERNALLY, () => {
            this.onCallFwdSettingsUpdatedExternally();
        });
        LocalEvent.setListener(LocalEvent.eventTypes.LAYOUT_MANAGER_INITIAL_SCRIPTS_LOADED, async () => {
            await this.onInitialScriptsLoaded();
        });

        // let the slice set up, then arm the key events interceptor
        setTimeout(() => {
            this.armShortcutRecorder();
        });

        this.slice = createSlice({
            name: this.sliceName,
            initialState,
            reducers: {
                toggleLoading(state, action) {
                    state.dataIsLoading = action.payload;
                },
                toggleDataUpdating(state, action) {
                    state.dataIsUpdating = action.payload;
                },
                toggleProfilePictureLoading(state, action) {
                    state.settings.profilePicture.dataIsLoading = action.payload;
                },
                /**
                 * Toggle a boolean parameter which is a subfield of a state settings object.
                 * @param {object} state
                 * @param {{payload: {field: string, subField: string, value: boolean}}} action
                 */
                toggleBooleanSubField(state, action) {
                    state.settings[action.payload.field][action.payload.subField] = action.payload.value;
                },
                setEditedShortcut(state, action) {
                    state.shortcutEditorData.editedShortcut = action.payload;

                    if (
                        state.shortcutEditorData.editedShortcut !== null &&
                        state.shortcutEditorData.editedShortcut.enabled
                    ) {
                        state.shortcutEditorData.canUseRecordedKey = true;
                        state.recordedShortcut = { ...action.payload };
                    } else {
                        state.shortcutEditorData.canUseRecordedKey = false;
                        state.recordedShortcut = null;
                    }
                },
                setDeletedShortcut(state, action) {
                    state.shortcutEditorData.deletedShortcut = action.payload;
                },
                persistDeletedShortcut(state, action) {
                    state.dataIsUpdating = false;
                    state.shortcutEditorData.deletedShortcut = null;

                    if (action.payload.result) {
                        state.settingsJson.keyboardShortcuts[action.payload.deletedIndex].enabled = false;
                    }

                    LocalEvent.trigger(LocalEvent.eventTypes.SHORTCUT_KEY_SAVED, action.payload.savedShortcut);
                },
                persistSavedShortcut(state, action) {
                    state.dataIsUpdating = false;
                    state.shortcutEditorData.editedShortcut = null;

                    if (action.payload.result) {
                        state.settingsJson.keyboardShortcuts[action.payload.savedIndex] = action.payload.savedShortcut;
                    }

                    LocalEvent.trigger(LocalEvent.eventTypes.SHORTCUT_KEY_SAVED, action.payload.savedShortcut);
                },
                setRecordedShortcut(state, action) {
                    state.recordedShortcut = action.payload;

                    if (state.shortcutEditorData.editedShortcut === null) {
                        state.shortcutEditorData.canUseRecordedKey = false;
                    } else {
                        const existingShortcuts = state.settingsJson.keyboardShortcuts.filter(
                            (shortcut) =>
                                state.recordedShortcut !== null &&
                                shortcut.enabled &&
                                UserSettingsSliceHandler.getInstance().shortcutsMatch(
                                    shortcut,
                                    state.recordedShortcut,
                                    null,
                                ) &&
                                state.shortcutEditorData.editedShortcut.type !== shortcut.type,
                        );

                        state.shortcutEditorData.canUseRecordedKey = existingShortcuts.length === 0;
                    }
                },
                setProfilePicture(state, action) {
                    state.settings.profilePicture.url = action.payload;
                },
                setPictureTimestamp(state, action) {
                    state.settings.profilePicture.timestamp = action.payload;
                },
                setLocalizedCountryNames(state, action) {
                    state.settings.languageSelector.localizedCountriesNames = action.payload;
                },
                /**
                 * Set a value of a setting property.
                 * @param state
                 * @param action {{payload:{name: string, value: any}}}
                 */
                setSettingsJsonValue(state, action) {
                    switch (typeof action.payload.value) {
                        case 'object':
                            state.settingsJson[action.payload.name] = [...action.payload.value];
                            break;

                        default:
                            state.settingsJson[action.payload.name] = action.payload.value;
                            break;
                    }
                },
                setAllJsonSettings(state, action) {
                    state.settingsJson = { ...state.settingsJson, ...action.payload };
                },
            },
        });
    }

    /**
     * Load the user settings JSON.
     * This is critical for the application, that why will be done at the beining.
     * @return {Promise<any>}
     */
    loadUserSettingsJson() {
        return new Promise((resolve, reject) => {
            webchatSDK.STWAPI.getUserUISettings().then(
                /**
                 * @param {{data: {results: UserSettingsJson}}} rawData
                 */
                (rawData) => {
                    const dbSettingsJson = rawData.data.results;

                    // set the rest of the values inside owr state
                    /**
                     * @type {{UserSettingsJson}}
                     */
                    const localState = this.getOwnState().settingsJson;

                    const newState = {};

                    Object.keys(dbSettingsJson).forEach((settingName) => {
                        // musts validate and format each fields individually (because those may be messy)
                        const settingValue = dbSettingsJson[settingName];
                        let valueToSet;

                        if (typeof localState[settingName] === 'undefined') {
                            return;
                        }

                        // use only 0 1 for booleans everywhere
                        if (typeof settingValue === 'boolean') {
                            valueToSet = settingValue === true ? 1 : 0;
                        } else {
                            valueToSet = settingValue;
                        }

                        newState[settingName] = valueToSet;
                    });

                    this.dispatchMultiple(
                        this.action('setAllJsonSettings', newState),
                        this.action('toggleLoading', false),
                    );

                    resolve();
                },
                (err) => {
                    Log.error(
                        'USER SETTINGS STORE',
                        `There was an error while loading user JSON settings: ${JSON.stringify(err)}`,
                    );

                    reject(err);
                },
            );
        });
    }

    /**
     * @return {UserSettingsJson|*}
     */
    getUserSettingsJson = () => this.getOwnState().settingsJson;

    /**
     * Dispatch the action that toggles a boolean subfield.
     * @param {string} field
     * @param {string} subField
     * @param {boolean} value
     */
    dispatchSubFieldToggle(field, subField, value) {
        this.dispatch('toggleBooleanSubField', { field, subField, value });
    }

    onCompanySettingsUpdate() {
        const companySettings = getCompanySettings();
        const { PictureTimestamp } = companySettings;

        this.checkFeatures(companySettings);

        this.action('toggleProfilePictureLoading', true);

        const actualTimestamp = this.getOwnState().settings.profilePicture.timestamp;

        if (PictureTimestamp > 0) {
            if (PictureTimestamp !== actualTimestamp) {
                this.dispatch('toggleProfilePictureLoading', true);

                webchatSDK.STWAPI.getSubscriberPicture(companySettings.Msisdn)
                    .then((pictureData) => {
                        this.dispatchMultiple(
                            this.action(
                                'setProfilePicture',
                                `data:${pictureData.data.results.type};base64, ${pictureData.data.results.content}`,
                            ),
                            this.action('setPictureTimestamp', PictureTimestamp),
                            this.action('toggleProfilePictureLoading', false),
                        );
                    })
                    .catch((e) => {
                        Log.notice(
                            'USER SETTINGS STORE',
                            `There was an error while loading the logged in user picture: ${e.message}`,
                        );
                    });
            }
        } else {
            this.dispatchMultiple(
                this.action('setProfilePicture', ``),
                this.action('setPictureTimestamp', 0),
                this.action('toggleProfilePictureLoading', false),
            );
        }
    }

    checkFeatures = (companySettings) => {
        const currentSettings = this.getOwnState().settingsJson;
        const settingsToUpdate = {};

        if (!companySettings.AllowVoip && currentSettings.defaultAction === USER_SETTINGS_OPTIONS.ACTIONS.CALL) {
            Log.notice(
                'USER SETTINGS STORE',
                `The defaultAction will be reset to "send message" because the Call feature has been disabled.`,
            );
            settingsToUpdate.defaultAction = USER_SETTINGS_OPTIONS.ACTIONS.MESSAGE;
        }
        if (!companySettings.AllowWalkieTalkie && currentSettings.defaultAction === USER_SETTINGS_OPTIONS.ACTIONS.PTT) {
            Log.notice(
                'USER SETTINGS STORE',
                `The defaultAction will be reset to "send message" because the Push-to-Talk feature has been disabled.`,
            );
            settingsToUpdate.defaultAction = USER_SETTINGS_OPTIONS.ACTIONS.MESSAGE;
        }

        if (
            (!companySettings.AllowWalkieTalkie && currentSettings.defaultPage === MainMenuItemType.CHANNELS) ||
            (!companySettings.IsDispatcher && currentSettings.defaultPage === MainMenuItemType.GEOLOCATION) ||
            (!companySettings.AllowMyBusiness && currentSettings.defaultPage === MainMenuItemType.MY_BUSINESS)
        ) {
            Log.notice(
                'USER SETTINGS STORE',
                `The defaultPage will be reset to "${MainMenuItemType.CONVERSATIONS}" because previously it was: 
                "${currentSettings.defaultPage}" and that feature has been disabled.`,
            );
            settingsToUpdate.defaultPage = MainMenuItemType.CONVERSATIONS;
        }

        if (Object.keys(settingsToUpdate).length) {
            this.dispatch('toggleDataUpdating', true);
            webchatSDK.STWAPI.setUserUISettings({ ...currentSettings, ...settingsToUpdate })
                .then(() => {
                    this.dispatch('setAllJsonSettings', settingsToUpdate);
                })
                .catch((e) => {
                    Log.error(
                        'USER SETTINGS STORE',
                        `There was an error while resetting the UI settings: ${JSON.stringify(e)}.`,
                    );
                })
                .finally(() => {
                    this.dispatch('toggleDataUpdating', false);
                });
        }
    };

    onCallFwdSettingsUpdatedExternally = () => this.toggleCallFwdDialogOpen(true);

    onInitialScriptsLoaded = async () => {
        if (!this.getOwnState().settings.languageSelector.localizedCountriesRequestedBeforeLayoutLoad) {
            // nothing to do at this stage
            return;
        }

        await this.loadLocalizedCountryNames();
    };

    loadLocalizedCountryNames = async () => {
        // localized names will load when layout manager loads
        if (!LayoutManager.brandingSummaryLoaded()) {
            this.dispatchSubFieldToggle('languageSelector', 'localizedCountriesRequestedBeforeLayoutLoad', true);

            return;
        }

        if (
            this.getOwnState().settings.languageSelector.dataIsLoading ||
            Object.keys(this.getOwnState().settings.languageSelector.localizedCountriesNames).length > 0
        ) {
            return;
        }

        this.dispatchSubFieldToggle('languageSelector', 'dataIsLoading', true);

        // try to load the localized country names;.
        // Ex: I want to switch to French => I should see Français (France) and not "French (France)"
        const promises = [];
        const supportedLanguages = ConfigIniUtil.getSupportedLanguages();
        const allLanguages = Object.keys(LayoutManager.getBrandingSummary().locales)
            .map((lang) => lang.replace('-', '_'))
            .filter((lang) => supportedLanguages.includes(lang));
        const localizedCountryNames = {};

        allLanguages.forEach((language) => {
            promises.push(LayoutManager.fetchLanguage(language));
        });

        await Promise.all(promises)
            .then((labels) => {
                allLanguages.forEach((language, index) => {
                    localizedCountryNames[language] = labels[index][`global_language_${language}`];
                });
            })
            .catch(() => {
                Log.warn(
                    'USER SETTINGS STORE',
                    'Unable to load the localized country names. Will use default names from current language',
                );

                allLanguages.forEach((language) => {
                    localizedCountryNames[language] = t(`global_language_${language}`);
                });
            })
            .finally(() => {
                this.dispatch('setLocalizedCountryNames', localizedCountryNames);
                this.dispatchSubFieldToggle('languageSelector', 'dataIsLoading', false);
            });
    };

    armShortcutRecorder() {
        window.addEventListener('keydown', (e) => {
            if (this.keyPressDebouncer !== null) {
                clearTimeout(this.keyPressDebouncer);
            }

            this.keyPressDebouncer = setTimeout(() => {
                const recordedShortcut = {};

                shortcutProps.forEach((prop) => {
                    if (e[prop]) {
                        recordedShortcut[prop] = e[prop];
                    }
                });

                this.dispatch('setRecordedShortcut', recordedShortcut);

                // check if the given key press match any shortcut
                let matchedShortcut = null;
                this.getOwnState().settingsJson.keyboardShortcuts.forEach((shortcut) => {
                    // react on first matching shortcut
                    if (matchedShortcut !== null) {
                        return;
                    }

                    // https://jira.streamwide.com/browse/SMARTMS-50693
                    if (shortcut.type === 'FLOOR_CONTROL') {
                        return;
                    }

                    if (!shortcut.enabled) {
                        return;
                    }

                    if (!this.shortcutsMatch(shortcut, recordedShortcut, e)) {
                        return;
                    }

                    matchedShortcut = shortcut;
                });

                if (matchedShortcut !== null) {
                    Log.notice('USER SETTINGS STORE', `The shortcut ${matchedShortcut.type} has been triggered.`);

                    LocalEvent.trigger(LocalEvent.eventTypes.SHORTCUT_KEY_PRESSED, matchedShortcut);
                }

                this.keyPressDebouncer = null;
            }, 200);
        });
    }

    shortcutsMatch(shortcut, reference, keyboardEvent = null) {
        let result = true;

        shortcutProps.forEach((prop) => {
            if (!result) {
                return;
            }

            if (typeof shortcut[prop] === 'undefined') {
                // the property which not exists in existing shortcut is assimilated as FALSE
                // which does not match with the keyboard event
                if (prop !== 'code') {
                    if (
                        (keyboardEvent !== null && keyboardEvent[prop] === true) ||
                        (typeof reference[prop] !== 'undefined' && reference[prop] === true)
                    ) {
                        result = false;
                    }
                }

                return;
            }

            result = shortcut[prop] === reference[prop];
        });

        return result;
    }

    // EXPORTABLE INTERFACE defined with arrow functions

    deleteShortcut = (shortcutToDelete) => {
        this.dispatch('toggleDataUpdating', true);

        const payload = [];
        let deletedIndex;

        this.getOwnState().settingsJson.keyboardShortcuts.forEach((shortcut, index) => {
            const clone = { ...shortcut };

            if (clone.type === shortcutToDelete.type) {
                deletedIndex = index;
                clone.enabled = false;
            }

            payload.push(clone);
        });

        webchatSDK.STWAPI.updateUserUISettings('keyboardShortcuts', payload).then(
            () => {
                this.dispatch('persistDeletedShortcut', {
                    result: true,
                    deletedIndex,
                });
            },
            (err) => {
                Log.error(
                    'USER SETTINGS STORE',
                    `There was an error while saving keyboard shortcuts: ${JSON.stringify(err)}.`,
                );

                this.dispatch('persistDeletedShortcut', { result: false });
            },
        );
    };

    /**
     * Save the current recorded key in the edited shortcut.
     */
    saveEditedShortcut = () => {
        this.dispatch('toggleDataUpdating', true);

        const payload = [];
        let savedIndex;
        let savedShortcut;

        const state = this.getOwnState();

        state.settingsJson.keyboardShortcuts.forEach((shortcut, index) => {
            let clone = { ...shortcut };

            if (clone.type === state.shortcutEditorData.editedShortcut.type) {
                savedIndex = index;
                clone = {
                    ...state.recordedShortcut,
                    ...{
                        enabled: true,
                        type: state.shortcutEditorData.editedShortcut.type,
                    },
                };
                savedShortcut = clone;
            }

            payload.push(clone);
        });

        webchatSDK.STWAPI.updateUserUISettings('keyboardShortcuts', payload).then(
            () => {
                this.dispatch('persistSavedShortcut', {
                    result: true,
                    savedIndex,
                    savedShortcut,
                });
            },
            (err) => {
                Log.error(
                    'USER SETTINGS STORE',
                    `There was an error while saving keyboard shortcuts: ${JSON.stringify(err)}.`,
                );

                this.dispatch('persistSavedShortcut', { result: false });
            },
        );
    };

    /**
     * Get the localized text associated with a keyboard shortcut.
     * @param {KeyboardShortcut} shortcut
     * @return string
     */
    getShortcutDescription = (shortcut, ignoreEnableState = false) => {
        if (shortcut === null) {
            return '';
        }

        if (!ignoreEnableState && !shortcut.enabled) {
            return t('global_inactive');
        }

        const labels = [];

        if (shortcut.ctrlKey === true) {
            labels.push(t('key_Control'));
        }

        if (shortcut.shiftKey === true) {
            labels.push(t('key_Shift'));
        }

        if (shortcut.metaKey === true) {
            labels.push(t('key_Meta'));
        }

        if (
            typeof shortcut.code !== 'undefined' &&
            ['ControlLeft', 'ControlRight', 'ShiftLeft', 'ShiftRight'].indexOf(shortcut.code) < 0
        ) {
            if (shortcut.code.toLowerCase().indexOf('meta') >= 0) {
                labels.push(t('key_Meta'));
            } else {
                const translated = t(`key_${shortcut.code}`);

                if (translated.indexOf('key_') >= 0) {
                    Log.warn('USER SETTINGS STORE', `The key ${shortcut.code} has no translation`);
                    labels.push(shortcut.code);
                } else {
                    labels.push(translated);
                }
            }
        }

        return labels.join(' + ');
    };

    submitProfilePicture = (encodedImage, croppedImage) => {
        this.dispatch('toggleProfilePictureLoading', true);
        return webchatSDK.STWAccountManager.updateUserPicture(encodedImage)
            .then(() => {
                this.dispatchMultiple(
                    this.action('setProfilePicture', croppedImage),
                    this.action('setPictureTimestamp', 0),
                );
                addSnackbar({
                    type: SnackbarTypes.SUCCESS,
                    title: t('settings_profile_image'),
                    message: t('js_all_user_picture_changed'),
                });
            })
            .catch(() => {
                addSnackbar({
                    type: SnackbarTypes.ERROR,
                    title: t('settings_profile_image'),
                    message: t('settings_profile_image_update_server_error'),
                });
            })
            .finally(() => this.dispatch('toggleProfilePictureLoading', false));
    };

    deleteProfilePicture = () => {
        this.dispatch('toggleProfilePictureLoading', true);
        return webchatSDK.STWAccountManager.deleteUserPicture()
            .then(() => {
                this.store.dispatch(this.slice.actions.setProfilePicture(''));
                addSnackbar({
                    type: SnackbarTypes.SUCCESS,
                    title: t('settings_profile_image'),
                    message: t('settings_profile_image_remove_success'),
                });
            })
            .catch(() => {
                addSnackbar({
                    type: SnackbarTypes.ERROR,
                    title: t('settings_profile_image'),
                    message: t('settings_profile_image_remove_error'),
                });
            })
            .finally(() => {
                this.dispatch('toggleProfilePictureLoading', false);
            });
    };

    updateAlias = (newAlias) => {
        this.dispatchSubFieldToggle('alias', 'dataIsSaving', true);
        return webchatSDK.STWAccountManager.updateUserAlias(newAlias)
            .then(() => {
                addSnackbar({
                    type: SnackbarTypes.SUCCESS,
                    title: t('settings_alias'),
                    message: t('settings_alias_changed_message'),
                });
                Log.notice('USER SETTINGS STORE', `The alias has been updated with value: ${newAlias}`);
            })
            .catch((e) => {
                addSnackbar({
                    type: SnackbarTypes.ERROR,
                    title: t('settings_alias'),
                    message: t('global_unexpected_error'),
                });
                Log.error(
                    'USER SETTINGS STORE',
                    `An error was encountered while updating the alias: ${JSON.stringify(e)}`,
                );
            })
            .finally(() => {
                this.dispatchSubFieldToggle('alias', 'dataIsSaving', false);
            });
    };

    updateCustomStatus = (newStatus) => {
        this.dispatchSubFieldToggle('userCustomStatus', 'dataIsSaving', true);
        return webchatSDK.STWAccountManager.updateUserCustomStatus(newStatus)
            .then(() => {
                addSnackbar({
                    type: SnackbarTypes.SUCCESS,
                    title: t('settings_availability'),
                    message: t('js_all_user_about_changed'),
                });
                Log.notice('USER SETTINGS STORE', `The custom status has been updated with value: ${newStatus}`);
            })
            .catch((e) => {
                addSnackbar({
                    type: SnackbarTypes.ERROR,
                    title: t('settings_availability'),
                    message: t('global_unexpected_error'),
                });
                Log.error(
                    'USER SETTINGS STORE',
                    `An error was encountered while updating the custom status: ${JSON.stringify(e)}`,
                );
            })
            .finally(() => {
                this.dispatchSubFieldToggle('userCustomStatus', 'dataIsSaving', false);
            });
    };

    updateAvailability = (newAvailability) => {
        this.dispatchSubFieldToggle('availability', 'dataIsSaving', true);
        return webchatSDK.STWAccountManager.updateUserAvailability(newAvailability)
            .then(() => {
                addSnackbar({
                    type: SnackbarTypes.SUCCESS,
                    title: t('settings_status'),
                    message: t('js_all_user_status_changed'),
                });
                Log.notice('USER SETTINGS STORE', `The availability has been updated with value: ${newAvailability}`);
            })
            .catch((e) => {
                addSnackbar({
                    type: SnackbarTypes.ERROR,
                    title: t('settings_status'),
                    message: t('global_unexpected_error'),
                });
                Log.error(
                    'USER SETTINGS STORE',
                    `An error was encountered while updating the availability: ${JSON.stringify(e)}`,
                );
            })
            .finally(() => {
                this.dispatchSubFieldToggle('availability', 'dataIsSaving', false);
            });
    };

    updateOperationalStatus = (newOperationalStatus) => {
        this.dispatchSubFieldToggle('operationalStatus', 'dataIsSaving', true);
        return webchatSDK.STWAccountManager.updateUserOperationalStatusById(newOperationalStatus)
            .then(() => {
                addSnackbar({
                    type: SnackbarTypes.SUCCESS,
                    title: t('settings_operational_status'),
                    message: t('js_all_user_operational_status_changed'),
                });
                Log.notice(
                    'USER SETTINGS STORE',
                    `The operational status has been updated with value: ${newOperationalStatus}`,
                );
            })
            .catch((e) => {
                addSnackbar({
                    type: SnackbarTypes.ERROR,
                    title: t('settings_operational_status'),
                    message: t('global_unexpected_error'),
                });
                Log.error(
                    'USER SETTINGS STORE',
                    `An error was encountered while updating the operational status: ${JSON.stringify(e)}`,
                );
            })
            .finally(() => {
                this.dispatchSubFieldToggle('operationalStatus', 'dataIsSaving', false);
            });
    };

    changePassword = (oldPassword, newPassword) => {
        this.dispatchSubFieldToggle('userPassword', 'dataIsSaving', true);
        return webchatSDK.STWAccountManager.changePassword(oldPassword, newPassword)
            .then(() => {
                addSnackbar({
                    type: SnackbarTypes.SUCCESS,
                    title: t('settings_password_title'),
                    message: t('settings_password_change_success'),
                });

                Log.notice('USER SETTINGS STORE', 'The user password has been updated');
            })
            .catch((e) => {
                addSnackbar({
                    type: SnackbarTypes.ERROR,
                    title: t('settings_password_title'),
                    message: t('settings_password_change_error'),
                });
                Log.error(
                    'USER SETTINGS STORE',
                    `An error was encountered while updating the user password: ${JSON.stringify(e)}`,
                );
            })
            .finally(() => {
                this.dispatchSubFieldToggle('userPassword', 'dataIsSaving', false);
            });
    };

    /**
     * Update the value of a setting.
     * @param {string} name The name of the updates setting
     * @param {any} value
     * @param {string} snackbarTitle The title of the snackbar which should be shown when saving was done. (if any)
     * @param {string} snackbarMessage The message of the snackbar which should be shown when saving was done. (if any)
     */
    updateJsonSetting = (name, value, snackbarTitle = '', snackbarMessage = '') =>
        new Promise((resolve) => {
            webchatSDK.STWAPI.updateUserUISettings(name, value).then(() => {
                this.dispatch('setSettingsJsonValue', { name, value });

                setTimeout(() => {
                    LocalEvent.trigger(LocalEvent.eventTypes.USER_SETTING_UPDATED, {
                        name,
                        value,
                    });
                });

                resolve();

                if (snackbarTitle.length > 0 && snackbarMessage.length > 0) {
                    addSnackbar({
                        type: SnackbarTypes.SUCCESS,
                        title: snackbarTitle,
                        message: snackbarMessage,
                    });
                }
            });
        });

    toggleCallFwdDialogOpen = (state) => this.dispatchSubFieldToggle('callFwd', 'dialogOpen', state);

    /**
     * Persist on server the changes made by user in call FWD.
     * @param {CallForwardingProfileData[]} settings
     */
    saveCallFwdSettings = (settings) =>
        new Promise((resolve) => {
            // due to an issue in the SDK, the settings must have a contact object set intead of the 'number' property
            const settingsToSave = [...settings];

            settingsToSave.forEach((setting, index) => {
                if (setting.number) {
                    settingsToSave[index] = { ...setting, ...{ contact: { msisdn: setting.number } } };
                }
            });

            webchatSDK.STWAccountManager.setCallForwardingSettings(settingsToSave)
                .then(() => {
                    LocalEvent.trigger(LocalEvent.eventTypes.USER_SETTING_UPDATED, {
                        name: 'call-fwd-settings',
                        value: settings,
                    });

                    addSnackbar({
                        type: 'success',
                        title: t('settings_advanced_telephony'),
                        message: t('settings_call_forward_settings_saved_ok'),
                    });

                    resolve();

                    setTimeout(() => this.toggleCallFwdDialogOpen(false));
                })
                .catch((e) => {
                    Log.error(
                        'USER SETTINGS STORE',
                        `An error was encountered while updating call fwd. settings: ${JSON.stringify(e)}`,
                    );

                    addSnackbar({
                        type: 'error',
                        title: t('settings_advanced_telephony'),
                        message: t('error_retrieve_subscriber_message_list'),
                    });

                    // still resolve; the dialog remains open
                    resolve();
                });
        });

    toggleCrossMuteListDialogOpen = (state) => this.dispatchSubFieldToggle('crossMuteList', 'dialogOpen', state);
}
// the handler
export const userSettingsSliceHandler = UserSettingsSliceHandler.getInstance();
// the selectors to be used in useSelect
export const selectDataIsLoading = (state) => state.userSettingsSlice.dataIsLoading;
export const selectDataIsUpdating = (state) => state.userSettingsSlice.dataIsUpdating;
export const selectShortcutEditorData = (state) => state.userSettingsSlice.shortcutEditorData;
export const selectKeyboardShortcuts = (state) => state.userSettingsSlice.settingsJson.keyboardShortcuts;
export const selectCallFwdDialogOpen = (state) => state.userSettingsSlice.settings.callFwd.dialogOpen;
export const selectCrossMuteListDialogOpen = (state) => state.userSettingsSlice.settings.crossMuteList.dialogOpen;
export const selectRecordedShortcut = (state) => state.userSettingsSlice.recordedShortcut;
export const selectProfilePicture = (state) => state.userSettingsSlice.settings.profilePicture;
export const selectPasswordSaving = (state) => state.userSettingsSlice.settings.userPassword.dataIsSaving;
export const selectAliasSaving = (state) => state.userSettingsSlice.settings.alias.dataIsSaving;
export const selectUserCustomStatusSaving = (state) => state.userSettingsSlice.settings.userCustomStatus.dataIsSaving;
export const selectAvailabilitySaving = (state) => state.userSettingsSlice.settings.availability.dataIsSaving;
export const selectOperationalStatusSaving = (state) => state.userSettingsSlice.settings.operationalStatus.dataIsSaving;
export const selectLocalizedCountriesNames = (state) =>
    state.userSettingsSlice.settings.languageSelector.localizedCountriesNames;
export const selectLanguageSelectorDataIsLoading = (state) =>
    state.userSettingsSlice.settings.languageSelector.dataIsLoading;
export const selectSettingsJson = (state) => state.userSettingsSlice.settingsJson;
// other named exports
export const { setEditedShortcut, setDeletedShortcut, setLocalizedCountryNames, toggleBooleanSubField } =
    userSettingsSliceHandler.slice.actions;
export const {
    deleteShortcut,
    saveEditedShortcut,
    getShortcutDescription,
    changePassword,
    submitProfilePicture,
    deleteProfilePicture,
    updateAlias,
    updateCustomStatus,
    updateAvailability,
    updateOperationalStatus,
    loadLocalizedCountryNames,
    updateJsonSetting,
    toggleCallFwdDialogOpen,
    toggleCrossMuteListDialogOpen,
    getUserSettingsJson,
    saveCallFwdSettings,
} = userSettingsSliceHandler;
